import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { hasAnyRole } from '../../../lib/roles'
import TierEditor from '../../../components/pricing/TierEditor'
import { loadTiers, saveTiers, validateTiers, type TierDraft } from '../../../lib/pricingTiers'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import SupplierPicker, { type PartyOption } from './SupplierPicker'
import { RateLineExtraHeaders, RateLineExtraCells } from '../../../components/pricing/RateLineExtraCells'
import { blankLineFlags } from '../../../lib/rateLineFields'
import CardPortsPicker from '../../../components/pricing/CardPortsPicker'
import { loadCardPorts, saveCardPorts, type PortOption } from '../../../lib/cardPorts'
import type {
  SupplierRateCard, SupplierRateLine, PricingCategory, PricingLane, PricingChargeCode, PricingModeScopedOption, RateCardStatus, RateLineUnit,
} from '../Pricing/types'
import { RATE_LINE_UNIT_OPTIONS, RATE_CARD_STATUS_OPTIONS } from '../Pricing/types'
import { fetchAllRows } from '../../../lib/fetchAllRows'

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
const cellInputCls = 'w-full border border-gray-200 dark:border-gray-600 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'

interface ContainerType { id: string; code: string }

type CardForm = Omit<SupplierRateCard, 'id' | 'created_at' | 'modified_at' | 'created_by'>

// postcode_prefix is a plain string here ('' = no prefix = the supplier's fallback price) and is
// converted to NULL on save. tiers are edited in place (TierEditor) and saved per line.
interface LineRow extends Omit<SupplierRateLine, 'id' | 'rate_card_id' | 'postcode_prefix'> {
  id?: string
  postcode_prefix: string
  tiers: TierDraft[]
  _hadTiers: boolean
  _key: string
}

interface AuditEntry { id: string; operation: string; user_email: string | null; changed_fields: string[] | null; created_at: string }

const normalisePrefix = (v: string) => v.toUpperCase().replace(/[^A-Z0-9]/g, '')

function emptyCardForm(): CardForm {
  return {
    supplier_id: '', mode_id: '', clearance_type_id: null, delivery_method_id: null, lane_id: null, rate_card_name: '',
    effective_from: new Date().toISOString().slice(0, 10), effective_to: null, currency: 'GBP', status: 'DRAFT', notes: '',
  }
}

function emptyLine(): LineRow {
  return { charge_code_id: '', unit: 'flat', container_type_id: null, postcode_prefix: '', unit_price: null, min_charge: null, max_charge: null, notes: '', ...blankLineFlags(), tiers: [], _hadTiers: false, _key: crypto.randomUUID() }
}

export default function SupplierRateCardsTab() {
  const { userRoles } = useAuth()
  const canWrite = hasAnyRole(userRoles, ['ADMIN', 'ACCOUNTS']) // OPERATIONS is read-only (RLS enforces the same)

  const [view, setView] = useState<'list' | 'detail'>('list')
  const [cards, setCards] = useState<SupplierRateCard[]>([])
  const [modes, setModes] = useState<PricingCategory[]>([])
  const [lanes, setLanes] = useState<PricingLane[]>([])
  const [chargeCodes, setChargeCodes] = useState<PricingChargeCode[]>([])
  const [clearanceTypes, setClearanceTypes] = useState<PricingModeScopedOption[]>([])
  const [deliveryMethods, setDeliveryMethods] = useState<PricingModeScopedOption[]>([])
  const [containerTypes, setContainerTypes] = useState<ContainerType[]>([])
  const [parties, setParties] = useState<PartyOption[]>([])
  const [loading, setLoading] = useState(true)

  const [filterSupplier, setFilterSupplier] = useState('')
  const [filterMode, setFilterMode] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const [cardId, setCardId] = useState<string | null>(null)
  const [cardForm, setCardForm] = useState<CardForm | null>(null)
  const [lines, setLines] = useState<LineRow[]>([])
  const [tiersOpen, setTiersOpen] = useState<Set<string>>(new Set())
  const [audit, setAudit] = useState<AuditEntry[]>([])
  const [ports, setPorts] = useState<PortOption[]>([])
  const [savedStatus, setSavedStatus] = useState<RateCardStatus | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { scrollProps: lineScrollProps, contentRef: lineTableRef, bar: lineScrollBar } = useStickyHScroll([lines, tiersOpen])

  const loadLookups = useCallback(async () => {
    const [{ data: cats }, { data: laneRows }, { data: codes }, { data: ctypes }, { data: clearances }, { data: methods }, people] = await Promise.all([
      supabase.from('pricing_categories').select('*').eq('is_active', true).order('display_order'),
      supabase.from('pricing_lanes').select('*').eq('is_active', true).order('name'),
      supabase.from('pricing_charge_codes').select('*').eq('is_active', true).order('code'),
      supabase.from('container_types').select('id, code').eq('is_active', true).order('display_order'),
      supabase.from('pricing_clearance_types').select('*').eq('is_active', true).order('name'),
      supabase.from('pricing_delivery_methods').select('*').eq('is_active', true).order('name'),
      fetchAllRows<PartyOption>((from, to) =>
        supabase.from('customers').select('id, code, name, is_supplier').eq('is_active', true).order('name').order('id').range(from, to) as never),
    ])
    setModes((cats as PricingCategory[]) || [])
    setLanes((laneRows as PricingLane[]) || [])
    setChargeCodes((codes as PricingChargeCode[]) || [])
    setContainerTypes((ctypes as ContainerType[]) || [])
    setClearanceTypes((clearances as PricingModeScopedOption[]) || [])
    setDeliveryMethods((methods as PricingModeScopedOption[]) || [])
    setParties(((people || []) as PartyOption[]).map(p => ({ ...p, is_supplier: !!p.is_supplier })))
  }, [])

  const loadCards = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('supplier_rate_cards').select('*').order('rate_card_name')
    if (filterSupplier) query = query.eq('supplier_id', filterSupplier)
    if (filterMode) query = query.eq('mode_id', filterMode)
    if (filterStatus) query = query.eq('status', filterStatus)
    const { data } = await query
    setCards((data as SupplierRateCard[]) || [])
    setLoading(false)
  }, [filterSupplier, filterMode, filterStatus])

  useEffect(() => { loadLookups() }, [loadLookups])
  useEffect(() => { if (view === 'list') loadCards() }, [view, loadCards])

  const partyName = (id: string) => { const p = parties.find(x => x.id === id); return p ? `${p.code} – ${p.name}` : '—' }
  const modeName = (id: string) => modes.find(m => m.id === id)?.name_en || '—'
  const optionName = (opts: PricingModeScopedOption[], id: string | null) => id ? (opts.find(o => o.id === id)?.name || '—') : '—'

  const markSupplier = async (customerId: string) => {
    const { error: err } = await supabase.from('customers').update({ is_supplier: true }).eq('id', customerId)
    if (err) throw err
    setParties(ps => ps.map(p => p.id === customerId ? { ...p, is_supplier: true } : p))
  }

  const openNew = () => {
    setCardId(null); setCardForm(emptyCardForm()); setLines([emptyLine()]); setTiersOpen(new Set()); setAudit([]); setPorts([]); setSavedStatus(null); setError(null); setView('detail')
  }

  const openCard = async (id: string) => {
    setError(null)
    const [{ data: card }, { data: lineRows }, { data: auditRows }] = await Promise.all([
      supabase.from('supplier_rate_cards').select('*').eq('id', id).single(),
      supabase.from('supplier_rate_lines').select('*').eq('rate_card_id', id).order('postcode_prefix', { nullsFirst: true }).order('id'),
      supabase.from('audit_logs').select('id, operation, user_email, changed_fields, created_at')
        .eq('table_name', 'supplier_rate_cards').eq('record_id', id).order('created_at', { ascending: false }).limit(20),
    ])
    if (!card) { setError('Rate card not found.'); return }
    const rows = (lineRows as SupplierRateLine[]) || []
    let tiersByLine = new Map<string, TierDraft[]>()
    try { tiersByLine = await loadTiers('supplier_rate_tiers', rows.map(l => l.id)) } catch (e: any) { setError(e?.message || 'Could not load tiers') }
    setCardId(id)
    setCardForm(card as SupplierRateCard)
    setSavedStatus((card as SupplierRateCard).status)
    try { setPorts(await loadCardPorts('supplier_rate_card_ports', id)) } catch { setPorts([]) }
    setLines(rows.map(l => {
      const tiers = tiersByLine.get(l.id) || []
      return { ...l, postcode_prefix: l.postcode_prefix || '', tiers, _hadTiers: tiers.length > 0, _key: l.id }
    }))
    setTiersOpen(new Set())
    setAudit((auditRows as AuditEntry[]) || [])
    setView('detail')
  }

  const addLine = () => setLines(ls => [...ls, { ...emptyLine(), scope: cardForm?.delivery_method_id ? 'DELIVERY' : 'CONTAINER' }])
  const removeLine = (key: string) => setLines(ls => ls.filter(l => l._key !== key))
  const updateLine = (key: string, patch: Partial<LineRow>) => setLines(ls => ls.map(l => l._key === key ? { ...l, ...patch } : l))
  const toggleTiers = (key: string) => setTiersOpen(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n })

  const save = async (publish: boolean) => {
    if (!cardForm || !canWrite) return
    if (!cardForm.supplier_id || !cardForm.mode_id || !cardForm.rate_card_name.trim() || !cardForm.effective_from) {
      setError('Supplier, Mode, Rate Card Name, and Effective From are required.'); return
    }
    if (!cardForm.delivery_method_id && lines.some(l => l.scope === 'DELIVERY')) {
      setError('This card has DELIVERY lines, so it needs a Delivery Method. Set it, or change those lines\' scope.'); return
    }
    if (lines.some(l => !l.charge_code_id)) { setError('Every line needs a Charge Code.'); return }
    const wantStatus: RateCardStatus = publish ? 'ACTIVE' : cardForm.status
    if (wantStatus === 'ACTIVE') {
      const unpriced = lines.filter(l => l.is_enabled && !l.is_at_cost && l.unit_price == null)
      if (unpriced.length > 0) { setError(`Cannot activate: ${unpriced.length} enabled line(s) have no price (${unpriced.map(l => chargeCodes.find(c => c.id === l.charge_code_id)?.code).join(', ')}). Price them, mark them at-cost, or switch them off.`); return }
    }
    for (const l of lines) {
      const tierErr = validateTiers(l.tiers)
      if (tierErr) { setError(`${chargeCodes.find(c => c.id === l.charge_code_id)?.code || 'Line'}${l.postcode_prefix ? ` / ${l.postcode_prefix}` : ''}: ${tierErr}`); return }
    }
    // Same shape as the DB unique index, caught here so the message names the offending line.
    const seen = new Set<string>()
    for (const l of lines) {
      const k = [l.charge_code_id, l.unit, l.container_type_id || '', l.postcode_prefix].join('|')
      if (seen.has(k)) {
        setError(`Duplicate line: ${chargeCodes.find(c => c.id === l.charge_code_id)?.code} / ${l.unit} / ${l.postcode_prefix || 'fallback (no prefix)'} appears twice on this card.`); return
      }
      seen.add(k)
    }

    setSaving(true); setError(null)
    try {
      const payload = {
        supplier_id: cardForm.supplier_id, mode_id: cardForm.mode_id, clearance_type_id: cardForm.clearance_type_id,
        delivery_method_id: cardForm.delivery_method_id || null, lane_id: cardForm.lane_id, rate_card_name: cardForm.rate_card_name.trim(),
        effective_from: cardForm.effective_from, effective_to: cardForm.effective_to, currency: cardForm.currency,
        // Becoming ACTIVE is done last, after the lines are saved: the DB refuses to activate a card whose
        // stored lines are still unpriced, and the prices being typed here are not stored yet.
        status: wantStatus === 'ACTIVE' ? (savedStatus ?? 'DRAFT') : wantStatus, notes: cardForm.notes || null,
      }
      let savedCardId = cardId
      if (savedCardId) {
        const { error: err } = await supabase.from('supplier_rate_cards').update(payload).eq('id', savedCardId)
        if (err) throw err
      } else {
        const { data, error: err } = await supabase.from('supplier_rate_cards').insert(payload).select('id').single()
        if (err) throw err
        savedCardId = data!.id
      }

      const { data: existingLineRows } = await supabase.from('supplier_rate_lines').select('id').eq('rate_card_id', savedCardId)
      const keptIds = new Set(lines.filter(l => l.id).map(l => l.id))
      const toDelete = (existingLineRows || []).map(r => r.id).filter(id => !keptIds.has(id))
      if (toDelete.length > 0) {
        const { error: err } = await supabase.from('supplier_rate_lines').delete().in('id', toDelete)
        if (err) throw err
      }

      for (const line of lines) {
        const { tiers, _hadTiers, _key, id, postcode_prefix, ...rest } = line
        void _key
        const linePayload = { ...rest, postcode_prefix: postcode_prefix || null, rate_card_id: savedCardId }
        let lineId = id
        if (lineId) {
          const { error: err } = await supabase.from('supplier_rate_lines').update(linePayload).eq('id', lineId)
          if (err) throw err
        } else {
          const { data, error: err } = await supabase.from('supplier_rate_lines').insert(linePayload).select('id').single()
          if (err) throw err
          lineId = data!.id
        }
        if (tiers.length > 0 || _hadTiers) await saveTiers('supplier_rate_tiers', lineId!, tiers)
      }

      await saveCardPorts('supplier_rate_card_ports', savedCardId!, ports)
      if (wantStatus === 'ACTIVE' && payload.status !== 'ACTIVE') {
        const { error: err } = await supabase.from('supplier_rate_cards').update({ status: 'ACTIVE' }).eq('id', savedCardId!)
        if (err) throw err
      }

      await openCard(savedCardId!)
    } catch (err: any) {
      setError(err?.message || 'Unknown error')
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async (c: SupplierRateCard) => {
    if (!canWrite || !window.confirm(`Deactivate "${c.rate_card_name}"? It is kept (status ARCHIVED) but no longer used for pricing.`)) return
    const { error: err } = await supabase.from('supplier_rate_cards').update({ status: 'ARCHIVED' }).eq('id', c.id)
    if (err) { window.alert(err.message); return }
    loadCards()
  }

  const selectedMode = modes.find(m => m.id === cardForm?.mode_id)
  const showContainerType = !!selectedMode && selectedMode.code !== 'AIR'
  const lanesForMode = lanes.filter(l => l.category_id === cardForm?.mode_id)
  const optionsForMode = (opts: PricingModeScopedOption[], selectedId: string | null) =>
    opts.filter(o => o.id === selectedId || (!!selectedMode && o.applicable_modes.includes(selectedMode.code)))
  const suppliers = parties.filter(p => p.is_supplier)

  if (view === 'detail' && cardForm) {
    return (
      <div className="flex gap-6">
        <div className="flex-1 min-w-0">
          <button onClick={() => setView('list')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4">&larr; Supplier Rate Cards</button>
          {!canWrite && <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg text-sm">Read-only — only ADMIN and ACCOUNTS can edit supplier rates.</div>}
          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}

          <fieldset disabled={!canWrite} className="min-w-0">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{cardId ? 'Edit Supplier Rate Card' : 'New Supplier Rate Card'}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Supplier *</label>
                  <SupplierPicker value={cardForm.supplier_id} onChange={id => setCardForm(f => f && ({ ...f, supplier_id: id }))}
                    parties={parties} canWrite={canWrite} onMarkSupplier={markSupplier} disabled={!canWrite} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mode *</label>
                  <select className={inputCls} value={cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, mode_id: e.target.value, lane_id: null, clearance_type_id: null, delivery_method_id: null }))}>
                    <option value="">— Select —</option>
                    {modes.map(m => <option key={m.id} value={m.id}>{m.name_en}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Delivery Method <span className="text-gray-400 font-normal">(only needed when the card has DELIVERY lines)</span></label>
                  <select className={inputCls} value={cardForm.delivery_method_id || ''} disabled={!cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, delivery_method_id: e.target.value || null }))}>
                    <option value="">— None (port / container / HBL costs) —</option>
                    {optionsForMode(deliveryMethods, cardForm.delivery_method_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Clearance Type <span className="text-gray-400 font-normal">(blank = any)</span></label>
                  <select className={inputCls} value={cardForm.clearance_type_id || ''} disabled={!cardForm.mode_id} onChange={e => setCardForm(f => f && ({ ...f, clearance_type_id: e.target.value || null }))}>
                    <option value="">— Any —</option>
                    {optionsForMode(clearanceTypes, cardForm.clearance_type_id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Lane <span className="text-gray-400 font-normal">(blank = all lanes)</span></label>
                  <select className={inputCls} value={cardForm.lane_id || ''} onChange={e => setCardForm(f => f && ({ ...f, lane_id: e.target.value || null }))}>
                    <option value="">— All lanes —</option>
                    {lanesForMode.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ports <span className="text-gray-400 font-normal">(discharge ports this card is quoted for)</span></label>
                  <CardPortsPicker value={ports} onChange={setPorts} disabled={!canWrite} />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Card Name *</label>
                  <input className={inputCls} value={cardForm.rate_card_name} onChange={e => setCardForm(f => f && ({ ...f, rate_card_name: e.target.value }))} placeholder='e.g. "Yodel pallet delivery 2026"' />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective From *</label>
                  <input type="date" className={inputCls} value={cardForm.effective_from} onChange={e => setCardForm(f => f && ({ ...f, effective_from: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective To</label>
                  <input type="date" className={inputCls} value={cardForm.effective_to || ''} onChange={e => setCardForm(f => f && ({ ...f, effective_to: e.target.value || null }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency</label>
                  <input className={inputCls} value={cardForm.currency} onChange={e => setCardForm(f => f && ({ ...f, currency: e.target.value.toUpperCase().slice(0, 3) }))} maxLength={3} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select className={inputCls} value={cardForm.status} onChange={e => setCardForm(f => f && ({ ...f, status: e.target.value as RateCardStatus }))}>
                    {RATE_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                  <textarea className={inputCls} rows={2} value={cardForm.notes || ''} onChange={e => setCardForm(f => f && ({ ...f, notes: e.target.value }))} />
                </div>
              </div>
            </div>
          </fieldset>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">Line Items</h3>
              <div className="overflow-x-auto" {...lineScrollProps}>
                <table ref={lineTableRef as React.RefObject<HTMLTableElement>} className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <th className="px-2 py-2">Charge Code</th>
                      <th className="px-2 py-2">Unit</th>
                      <RateLineExtraHeaders />
                      {showContainerType && <th className="px-2 py-2">Container</th>}
                      <th className="px-2 py-2">Postcode Prefix</th>
                      <th className="px-2 py-2">Price</th>
                      <th className="px-2 py-2">Min</th>
                      <th className="px-2 py-2">Max</th>
                      <th className="px-2 py-2">Notes</th>
                      <th className="px-2 py-2">Tiers</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {lines.map(line => (
                      <LineRows key={line._key} line={line} colSpan={showContainerType ? 14 : 13} open={tiersOpen.has(line._key)} readOnly={!canWrite}
                        currency={cardForm.currency} showContainerType={showContainerType} chargeCodes={chargeCodes} containerTypes={containerTypes}
                        onChange={patch => updateLine(line._key, patch)} onRemove={() => removeLine(line._key)} onToggleTiers={() => toggleTiers(line._key)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <StickyHScrollbar {...lineScrollBar} />
              {canWrite && <button onClick={addLine} className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline">+ Add Line</button>}
              <p className="mt-2 text-xs text-gray-400">
                Postcode Prefix: upper-case letters/digits, e.g. <span className="font-mono">B</span> (all Birmingham), <span className="font-mono">B76</span>, <span className="font-mono">BT</span> (Northern Ireland).
                Leave blank for the supplier's fallback price. The longest matching prefix wins; <span className="font-mono">B</span> does not cover <span className="font-mono">BT</span>/<span className="font-mono">BS</span>.
              </p>
            </div>

          {canWrite && (
            <div className="flex justify-end gap-3">
              <button onClick={() => save(false)} disabled={saving} className="px-5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={() => save(true)} disabled={saving} className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save & Activate'}
              </button>
            </div>
          )}
        </div>

        <div className="w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white mb-3 uppercase tracking-wide">Version History</h3>
            {!cardId ? (
              <p className="text-xs text-gray-400">Save the card to start tracking history.</p>
            ) : audit.length === 0 ? (
              <p className="text-xs text-gray-400">No history yet.</p>
            ) : (
              <ul className="space-y-3">
                {audit.map(a => (
                  <li key={a.id} className="text-xs">
                    <div className="font-semibold text-gray-700 dark:text-gray-300">{a.operation}</div>
                    <div className="text-gray-400">{new Date(a.created_at).toLocaleString()}</div>
                    <div className="text-gray-400">{a.user_email || 'unknown'}</div>
                    {a.changed_fields && a.changed_fields.length > 0 && <div className="text-gray-400 mt-0.5">{a.changed_fields.join(', ')}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-end gap-3 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Supplier</label>
            <select className={inputCls} value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)}>
              <option value="">All suppliers</option>
              {suppliers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mode</label>
            <select className={inputCls} value={filterMode} onChange={e => setFilterMode(e.target.value)}>
              <option value="">All modes</option>
              {modes.map(m => <option key={m.id} value={m.id}>{m.name_en}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Status</label>
            <select className={inputCls} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All statuses</option>
              {RATE_CARD_STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        {canWrite && (
          <button onClick={openNew} disabled={modes.length === 0} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            + New Supplier Rate Card
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {['Rate Card', 'Supplier', 'Mode', 'Clearance', 'Delivery', 'Effective', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {cards.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{c.rate_card_name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{partyName(c.supplier_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{modeName(c.mode_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{c.clearance_type_id ? optionName(clearanceTypes, c.clearance_type_id) : 'Any'}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{optionName(deliveryMethods, c.delivery_method_id)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">{c.effective_from} {c.effective_to ? `→ ${c.effective_to}` : '(ongoing)'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      c.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      c.status === 'DRAFT' ? 'bg-gray-100 text-gray-600' :
                      c.status === 'EXPIRED' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 space-x-3">
                    <button onClick={() => openCard(c.id)} className="text-blue-600 hover:underline text-xs font-medium">{canWrite ? 'Edit' : 'View'}</button>
                    {canWrite && c.status !== 'ARCHIVED' && <button onClick={() => deactivate(c)} className="text-red-500 hover:underline text-xs font-medium">Deactivate</button>}
                  </td>
                </tr>
              ))}
              {cards.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No supplier rate cards found</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-2 text-xs text-gray-400">{cards.length} rate card{cards.length !== 1 ? 's' : ''}</div>
    </div>
  )
}

// One line = its main row plus (when expanded) a tier-editor row beneath it.
function LineRows({ line, colSpan, open, readOnly, currency, showContainerType, chargeCodes, containerTypes, onChange, onRemove, onToggleTiers }: {
  line: LineRow; colSpan: number; open: boolean; readOnly: boolean; currency: string; showContainerType: boolean
  chargeCodes: PricingChargeCode[]; containerTypes: ContainerType[]
  onChange: (patch: Partial<LineRow>) => void; onRemove: () => void; onToggleTiers: () => void
}) {
  const numOrNull = (v: string) => v === '' ? null : parseFloat(v) || 0
  return (
    <>
      <tr className={line.is_enabled ? '' : 'opacity-50'}>
        <td className="px-2 py-2">
          <select disabled={readOnly} className={cellInputCls} value={line.charge_code_id} onChange={e => onChange({ charge_code_id: e.target.value })}>
            <option value="">—</option>
            {chargeCodes.map(c => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
        </td>
        <td className="px-2 py-2">
          <select disabled={readOnly} className={cellInputCls} value={line.unit} onChange={e => onChange({ unit: e.target.value as RateLineUnit })}>
            {RATE_LINE_UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </td>
        <RateLineExtraCells line={line} disabled={readOnly} onChange={p => onChange(p.is_at_cost ? { ...p, unit_price: null } : p)} />
        {showContainerType && (
          <td className="px-2 py-2">
            <select disabled={readOnly} className={cellInputCls} value={line.container_type_id || ''} onChange={e => onChange({ container_type_id: e.target.value || null })}>
              <option value="">—</option>
              {containerTypes.map(ct => <option key={ct.id} value={ct.id}>{ct.code}</option>)}
            </select>
          </td>
        )}
        <td className="px-2 py-2">
          <input disabled={readOnly} className={cellInputCls + ' w-24 font-mono'} value={line.postcode_prefix} placeholder="fallback" maxLength={8}
            onChange={e => onChange({ postcode_prefix: normalisePrefix(e.target.value) })} />
        </td>
        <td className="px-2 py-2">
          <input disabled={readOnly || line.is_at_cost} type="number" step="0.01" className={cellInputCls + ' w-20'} value={line.unit_price ?? ''} placeholder={line.is_at_cost ? 'at cost' : 'not set'}
            onChange={e => onChange({ unit_price: e.target.value === '' ? null : parseFloat(e.target.value) })} />
        </td>
        <td className="px-2 py-2"><input disabled={readOnly} type="number" step="0.01" className={cellInputCls + ' w-16'} value={line.min_charge ?? ''} onChange={e => onChange({ min_charge: numOrNull(e.target.value) })} /></td>
        <td className="px-2 py-2"><input disabled={readOnly} type="number" step="0.01" className={cellInputCls + ' w-16'} value={line.max_charge ?? ''} onChange={e => onChange({ max_charge: numOrNull(e.target.value) })} /></td>
        <td className="px-2 py-2"><input disabled={readOnly} className={cellInputCls} value={line.notes || ''} onChange={e => onChange({ notes: e.target.value })} /></td>
        <td className="px-2 py-2 whitespace-nowrap">
          {/* Always enabled: read-only users may still view tiers, editing is blocked inside TierEditor */}
          <button type="button" onClick={onToggleTiers} className="text-blue-600 dark:text-blue-400 hover:underline">
            {open ? 'Hide' : 'Tiers'}{line.tiers.length > 0 ? ` (${line.tiers.length})` : ''}
          </button>
        </td>
        <td className="px-2 py-2">{!readOnly && <button type="button" onClick={onRemove} className="text-red-500 hover:underline text-xs">Remove</button>}</td>
      </tr>
      {open && (
        <tr className="bg-gray-50 dark:bg-gray-700/30">
          <td colSpan={colSpan} className="px-4 py-3">
            <TierEditor tiers={line.tiers} onChange={tiers => onChange({ tiers })} readOnly={readOnly} unitLabel={line.unit} currency={currency} />
          </td>
        </tr>
      )}
    </>
  )
}
